import { useEffect, useState } from "react"
import { Sparkles } from "lucide-react"
import { useBrandStore } from "@/stores/brand"
import { cn } from "@/lib/utils"

interface BrandLogoProps {
  /** "light" usa logoLight (versión clara para fondos oscuros); "default" usa logo. */
  variant?: "default" | "light"
  /** Tailwind classes para el wrapper que envuelve <img> y el fallback. */
  className?: string
  /** Tailwind para el <img>. Default: object-contain w-full h-full. */
  imgClassName?: string
  /** Tailwind para el fallback (cuando el logo no carga). */
  fallbackClassName?: string
  /** Icono del fallback. Default: <Sparkles>. */
  fallbackIcon?: React.ReactNode
}

/**
 * Logo de la marca activa (resuelta por host).
 *
 * Maneja automáticamente el caso de imagen rota / no encontrada cayendo a un
 * placeholder Sparkles. Resetea el estado de error cuando cambia la URL del
 * logo (evita que un fallo en la marca A persista al cambiar a marca B).
 */
export function BrandLogo({
  variant = "default",
  className,
  imgClassName = "w-full h-full object-contain",
  fallbackClassName,
  fallbackIcon,
}: BrandLogoProps) {
  const { brandConfig } = useBrandStore()
  const src =
    variant === "light"
      ? brandConfig?.logoLight || brandConfig?.logo
      : brandConfig?.logo

  const [errored, setErrored] = useState(false)
  // Si la URL cambia (e.g. cambio de marca), reintentar.
  useEffect(() => {
    setErrored(false)
  }, [src])

  if (!src || errored) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/60 shadow-lg",
          className,
          fallbackClassName
        )}
      >
        {fallbackIcon ?? <Sparkles className="w-1/2 h-1/2 text-white" />}
      </div>
    )
  }

  return (
    <div className={className}>
      <img
        src={src}
        alt={brandConfig?.name || "Logo"}
        className={imgClassName}
        onError={() => setErrored(true)}
      />
    </div>
  )
}
